import { Navigate, useRoutes } from "react-router-dom";
import { Suspense, lazy } from "react";

// layouts
import AuthLayout from "src/layouts/auth";
import LoadingScreen from "src/components/loadingScreen";
import AclGuard from "src/acl/AclGuard";

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  return (
    <Suspense
      fallback={
        <LoadingScreen
          sx={{
            top: 0,
            left: 0,
            width: 1,
            zIndex: 9999,
            position: "fixed",
          }}
        />
      }
    >
      <AclGuard aclAbilities={props.acl}>
        <Component {...props} />
      </AclGuard>
    </Suspense>
  );
};

export default function asRouter() {
  return useRoutes([
    {
      path: "*",
      element: <AuthLayout />,
      children: [
        { path: "404", element: <NotFound /> },
        { path: "*", element: <Navigate to="/404" replace /> },
      ],
    },
    {
      path: "/",
      element: <DLayout acl={{ subject: "sidebar" }} />,
      children: [
        { element: <Navigate to="/dashboard" replace /> },
        {
          path: "dashboard",
          element: <LandingPage acl={{ subject: "dashboard" }} />,
        },
        { path: "orders", element: <AllOrder acl={{ subject: "orders" }} /> },
        {
          path: "orders/:id",
          element: <OrderDetail acl={{ subject: "order_edit" }} />,
        },
        {
          path: "jobs",
          element: <Jobs acl={{ subject: "jobs" }} />,
        },
        {
          path: "jobs/:jid",
          element: <AddEditJobs acl={{ subject: "jobs" }} />,
        },
        {
          path: "timeline-stages",
          element: <TimelineStages acl={{ subject: "jobs" }} />,
        },
        {
          path: "consultation",
          element: <Consultation acl={{ subject: "consultation" }} />,
        },
        {
          path: "consultation/:id",
          element: (
            <ConsultationDetails acl={{ subject: "consultation_edit" }} />
          ),
        },
        {
          path: "products/:ptype/:pid",
          element: <ProductUpdate acl={{ subject: "product" }} />,
        },
        {
          path: "products",
          element: <Products acl={{ subject: "product" }} />,
        },
        {
          path: "products/add",
          element: <ProductUpdate acl={{ subject: "product" }} />,
        },

        {
          path: "categories",
          element: <Categories acl={{ subject: "categories" }} />,
        },
        {
          path: "categories/:cid",
          element: <AddCategory acl={{ subject: "categories" }} />,
        },
        {
          path: "categories/add",
          element: <AddCategory acl={{ subject: "categories" }} />,
        },
        {
          path: "slides",
          element: <MainSlides acl={{ subject: "slides" }} />,
        },
        {
          path: "city",
          element: <Locations acl={{ subject: "locations" }} />,
        },
        {
          path: "add_ons",
          element: <AddOns acl={{ subject: "addons" }} />,
        },
        {
          path: "projects",
          element: <Projects acl={{ subject: "projects" }} />,
        },
        { path: "banner", element: <Banner acl={{ subject: "banner" }} /> },
        {
          path: "slides/add",
          element: <AddPrimarySlider acl={{ subject: "slides" }} />,
        },
        {
          path: "slides/:sid",
          element: <EditPrimarySlider acl={{ subject: "slides" }} />,
        },
        { path: "brands", element: <Brands /> },
        { path: "brands/add", element: <AddBrands /> },
        { path: "brands/:bid", element: <EditBrands /> },
        {
          path: "events-inquiry",
          element: <EventsInquiry acl={{ subject: "events-inquiry" }} />,
        },
        {
          path: "events-inquiry/events/:eid",
          element: <EventProfile acl={{ subject: "events-inquiry" }} />,
        },
        {
          path: "events-inquiry/inquiry/:id",
          element: <InquiryProfile acl={{ subject: "events-inquiry" }} />,
        },
        {
          path: "employees",
          element: <Users acl={{ subject: "employees" }} />,
        },
        {
          path: "report",
          element: <ReportPage acl={{ subject: "report" }} />,
        },
        {
          path: "user",
          element: <Customer acl={{ subject: "user" }} />,
        },
        {
          path: "employees/:user_id",
          element: <AddEditUser acl={{ subject: "employees" }} />,
        },
        {
          path: "employees/add",
          element: <AddEditUser acl={{ subject: "employees" }} />,
        },
        // { path: "users/:id", element: <UserProfile /> },
        { path: "newsletter", element: <Newsletter /> },
        {
          path: "settings",
          element: <GeneralSettings acl={{ subject: "setting" }} />,
        },
        { path: "role", element: <Role acl={{ subject: "roles" }} /> },
        {
          path: "role/:role_id",
          element: <AddEditRole acl={{ subject: "roles" }} />,
        },
        {
          path: "role-options",
          element: <RoleOptions acl={{ subject: "roles" }} />,
        },
        {
          path: "role/add",
          element: <AddEditRole acl={{ subject: "roles" }} />,
        },
        {
          path: "role-options/add",
          element: <AddEditRoleOptions acl={{ subject: "roles" }} />,
        },
        {
          path: "document-category",
          element: <Document acl={{ subject: "document" }} />,
        },

        {
          path: "add-document",
          element: <AddDocument acl={{ subject: "document" }} />,
        },
        {
          path: "add-document/add",
          element: <AddDocumentAdd acl={{ subject: "document" }} />,
        },
        {
          path: "add-document/:id",
          element: <AddDocumentEdit acl={{ subject: "document" }} />,
        },
        {
          path: "zone",
          element: <Zone acl={{ subject: "zone" }} />,
        },
        {
          path: "seo",
          element: <Seo acl={{ subject: "seo" }} />,
        },
        {
          path: "faq",
          element: <FAQ acl={{ subject: "faq" }} />,
        },
        {
          path: "testimonials",
          element: <Testimonials acl={{ subject: "testimonials" }} />,
        },
        {
          path: "testimonials/add",
          element: <AddEditTestimonials acl={{ subject: "testimonials" }} />,
        },
        {
          path: "testimonials/:tid",
          element: <AddEditTestimonials acl={{ subject: "testimonials" }} />,
        },
        {
          path: "blog/blogs_events",
          element: <AllBlogs acl={{ subject: "blogs" }} />,
        },
        {
          path: "blog/blogs_events/add",
          element: <AddBlog acl={{ subject: "blogs" }} />,
        },
        {
          path: "blog/blogs_events/:blid",
          element: <EditBlog acl={{ subject: "blogs" }} />,
        },
        {
          path: "blog/blog_category",
          element: <BlogCategories acl={{ subject: "blogs" }} />,
        },
        {
          path: "/packages",
          element: <AllPackages acl={{ subject: "packages" }} />,
        },
        {
          path: "/packages/add",
          element: <AddPackage acl={{ subject: "packages" }} />,
        },
        {
          path: "/packages/:pid",
          element: <EditPackage acl={{ subject: "packages" }} />,
        },
      ],
    },
    {
      path: "/auth",
      element: <AuthLayout />,
      children: [
        { path: "login", element: <Login /> },
        {
          path: "/auth/forget-password",
          element: <ForgetPassword />,
        },
        {
          path: "/auth/reset-password",
          element: <ResetPassword />,
        },
        {
          path: "/auth/register",
          element: <Register />,
        },
      ],
    },
    { path: "*", element: <Navigate to="/404" replace /> },
  ]);
}
const Jobs = Loadable(lazy(() => import("src/pages/job")));
const AddEditJobs = Loadable(lazy(() => import("src/pages/job/addEditJobs")));
const TimelineStages = Loadable(
  lazy(() => import("src/pages/timelineStages"))
);
const DLayout = Loadable(lazy(() => import("src/layouts/dashboard")));
const AddEditUser = Loadable(lazy(() => import("src/pages/users/AddEditUser")));
const Users = Loadable(lazy(() => import("src/pages/users/index")));
const Customer = Loadable(lazy(() => import("src/pages/customers/index")));
const AddEditRoleOptions = Loadable(
  lazy(() => import("src/pages/roleOptions/addEditOptions"))
);
const AddEditRole = Loadable(lazy(() => import("src/pages/role/addEditRole")));
const AllOrder = Loadable(lazy(() => import("src/pages/orders")));
const OrderDetail = Loadable(
  lazy(() => import("src/pages/orders/orderDetail"))
);
const Role = Loadable(lazy(() => import("src/pages/role/index")));

const Locations = Loadable(lazy(() => import("src/pages/locations/index")));
const Banner = Loadable(lazy(() => import("src/pages/banner/index")));
const RoleOptions = Loadable(lazy(() => import("src/pages/roleOptions/index")));
const Products = Loadable(lazy(() => import("src/pages/products")));
const AddProduct = Loadable(
  lazy(() => import("src/pages/products/addProduct"))
);
const ProductUpdate = Loadable(
  lazy(() => import("src/pages/products/editProduct"))
);

const Categories = Loadable(lazy(() => import("src/pages/categories")));
const AddCategory = Loadable(
  lazy(() => import("src/pages/categories/editCategory"))
);
const Brands = Loadable(lazy(() => import("src/pages/brands")));

// const Users = Loadable(lazy(() => import("src/pages/users")));

const AddBrands = Loadable(lazy(() => import("src/pages/brands/addBrand.js")));
const EditBrands = Loadable(lazy(() => import("src/pages/brands/editBrand")));

const GeneralSettings = Loadable(
  lazy(() => import("src/pages/settings/general"))
);
const AppSettings = Loadable(
  lazy(() => import("src/pages/settings/application"))
);
const AddPrimarySlider = Loadable(
  lazy(() => import("src/pages/slides/homeSlides/addHomeSlide"))
);
const EditPrimarySlider = Loadable(
  lazy(() => import("src/pages/slides/homeSlides/editHomeSlide"))
);
const EditBanners = Loadable(
  lazy(() => import("src/pages/settings/application/homeBanners/editBanners"))
);
//packages
const AllPackages = Loadable(lazy(() => import("src/pages/packages")));
const AddPackage = Loadable(
  lazy(() => import("src/pages/packages/addPackage"))
);
const EditPackage = Loadable(
  lazy(() => import("src/pages/packages/editPackage"))
);
// blog
const BlogCategories = Loadable(lazy(() => import("src/pages/blogCategory")));
const AddBlog = Loadable(lazy(() => import("src/pages/blog/addBlog")));
const EditBlog = Loadable(lazy(() => import("src/pages/blog/editBlog")));
const AllBlogs = Loadable(lazy(() => import("src/pages/blog")));
const MainSlides = Loadable(lazy(() => import("src/pages/slides")));
// Main
const LandingPage = Loadable(lazy(() => import("src/pages")));

// notfound
const NotFound = Loadable(lazy(() => import("src/pages/404")));

// auth
const ForgetPassword = Loadable(
  lazy(() => import("src/pages/auth/forgetPassword"))
);
const ResetPassword = Loadable(
  lazy(() => import("src/pages/auth/resetPassword"))
);
const Login = Loadable(lazy(() => import("src/pages/auth/login")));
const Newsletter = Loadable(lazy(() => import("src/pages/newsletter")));
const Register = Loadable(lazy(() => import("src/pages/auth/register")));
const AddOns = Loadable(lazy(() => import("src/pages/addOns")));
const Projects = Loadable(lazy(() => import("src/pages/projects")));

// Document-section
const Document = Loadable(
  lazy(() => import("src/pages/documentCategory/index"))
);
const AddDocument = Loadable(lazy(() => import("src/pages/addDocument")));
const AddDocumentAdd = Loadable(
  lazy(() => import("src/pages/addDocument/addDocument"))
);
const AddDocumentEdit = Loadable(
  lazy(() => import("src/pages/addDocument/editDocument"))
);
const Consultation = Loadable(lazy(() => import("src/pages/consultation")));
const ConsultationDetails = Loadable(
  lazy(() => import("src/pages/consultation/consultationDetails"))
);
const Zone = Loadable(lazy(() => import("src/pages/zone")));
const Seo = Loadable(lazy(() => import("src/pages/seo")));
const FAQ = Loadable(lazy(() => import("src/pages/faq")));
const EventsInquiry = Loadable(lazy(() => import("src/pages/events-inquiry")));
const EventProfile = Loadable(
  lazy(() => import("src/pages/events-inquiry/eventProfile"))
);
const InquiryProfile = Loadable(
  lazy(() => import("src/pages/events-inquiry/inquiryProfile"))
);
const Testimonials = Loadable(lazy(() => import("src/pages/testimonials")));
const AddEditTestimonials = Loadable(
  lazy(() => import("src/pages/testimonials/addEditTestimonial"))
);
const ReportPage = Loadable(lazy(() => import("src/pages/report")));
